/**
 * Core audio artifact detection algorithms
 * Ported from Python backend/app/services/artifact_detection.py
 */

import { computeSTFT, averageMagnitudeInRange } from './fft'
import { calculateLUFS, STREAMING_TARGETS } from './lufs'

export interface ArtifactReport {
  sizzleScore: number // 0-1, energy ratio 5-8kHz vs full spectrum
  muddinessScore: number // 0-1, energy below 100Hz relative to 100-500Hz
  clickCount: number // Number of detected transient anomalies
  clickPositions: number[] // Timestamps in seconds
  dynamicRangeDb: number // Peak-to-RMS ratio
  lufs: number // Integrated loudness
  overallScore: number // 0-100 composite score
  duration: number // Track duration in seconds
  detectedSource: string | null // Detected AI model (Suno, Udio, etc.)
  recommendations: string[]
}

export interface AnalysisProgress {
  step: string
  percent: number
  detail?: string
}

export type ProgressCallback = (progress: AnalysisProgress) => void

/**
 * Minimum delay between steps to show progress (ms)
 */
const MIN_STEP_DELAY = 800

/**
 * Sleep utility for minimum progress visibility
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Calculate sizzle score (harsh 5-8kHz frequencies)
 * Uses STFT to measure energy ratio in the sizzle band
 */
function calculateSizzle(samples: Float32Array, sampleRate: number): number {
  const fftSize = 2048
  const hopLength = 512

  const { magnitudes, frequencies } = computeSTFT(
    samples,
    sampleRate,
    fftSize,
    hopLength
  )

  // Sizzle band: 5-8 kHz
  const sizzleEnergy = averageMagnitudeInRange(magnitudes, frequencies, 5000, 8000)

  // Reference band: full spectrum (excluding DC and very low)
  const fullEnergy = averageMagnitudeInRange(magnitudes, frequencies, 100, sampleRate / 2)

  // Ratio
  const ratio = sizzleEnergy / (fullEnergy + 1e-10)

  // Map to 0-1 score where higher = more sizzle
  // Calibrated based on typical values (clean audio ~0.2-0.4, sizzly ~0.6+)
  return Math.max(0, Math.min(1, (ratio - 0.2) / 0.4))
}

/**
 * Calculate muddiness score (excessive low frequencies)
 * Compares energy below 100Hz to 100-500Hz band
 */
function calculateMuddiness(samples: Float32Array, sampleRate: number): number {
  const fftSize = 2048
  const hopLength = 1024

  const { magnitudes, frequencies } = computeSTFT(
    samples,
    sampleRate,
    fftSize,
    hopLength
  )

  // Sub-bass: < 100Hz
  const subBassEnergy = averageMagnitudeInRange(magnitudes, frequencies, 20, 100)

  // Bass reference: 100-500Hz
  const bassEnergy = averageMagnitudeInRange(magnitudes, frequencies, 100, 500)

  // Ratio - higher means more mud relative to clean bass
  const ratio = subBassEnergy / (bassEnergy + 1e-10)

  // Score - 0.3 ratio is normal, 1.0+ is muddy
  return Math.max(0, Math.min(1, (ratio - 0.3) / 0.7))
}

/**
 * Detect clicks/pops in audio
 * Finds samples where the derivative exceeds threshold × std
 */
function detectClicks(
  samples: Float32Array,
  sampleRate: number,
  threshold: number = 4.0
): { count: number; positions: number[] } {
  // Calculate sample-to-sample difference
  const diff = new Float32Array(samples.length - 1)
  for (let i = 0; i < diff.length; i++) {
    diff[i] = samples[i + 1] - samples[i]
  }

  // Calculate standard deviation
  let sum = 0
  for (let i = 0; i < diff.length; i++) {
    sum += diff[i]
  }
  const mean = sum / diff.length

  let variance = 0
  for (let i = 0; i < diff.length; i++) {
    variance += (diff[i] - mean) ** 2
  }
  const std = Math.sqrt(variance / diff.length)

  const thresholdValue = threshold * std

  // Find clicks
  const clickIndices: number[] = []
  for (let i = 0; i < diff.length; i++) {
    if (Math.abs(diff[i]) > thresholdValue) {
      clickIndices.push(i)
    }
  }

  // Group nearby clicks (within 10ms)
  const minGap = Math.floor(sampleRate * 0.01) // 10ms in samples
  const positions: number[] = []
  let lastClick = -minGap

  for (const idx of clickIndices) {
    if (idx - lastClick >= minGap) {
      positions.push(idx / sampleRate) // Convert to seconds
      lastClick = idx
    }
  }

  return { count: positions.length, positions }
}

/**
 * Calculate dynamic range (peak-to-RMS ratio in dB)
 * Lower values indicate over-compression
 */
function calculateDynamicRange(samples: Float32Array): number {
  // Remove silence (threshold 0.01)
  const threshold = 0.01
  const activeSamples: number[] = []
  for (let i = 0; i < samples.length; i++) {
    if (Math.abs(samples[i]) > threshold) {
      activeSamples.push(samples[i])
    }
  }

  if (activeSamples.length === 0) {
    return 0.0
  }

  // Find peak
  let peak = 0
  for (const sample of activeSamples) {
    peak = Math.max(peak, Math.abs(sample))
  }

  // Calculate RMS
  let sumSquares = 0
  for (const sample of activeSamples) {
    sumSquares += sample * sample
  }
  const rms = Math.sqrt(sumSquares / activeSamples.length)

  if (rms < 1e-10) {
    return 0.0
  }

  // Peak-to-RMS in dB (crest factor)
  return 20 * Math.log10(peak / rms)
}

/**
 * Convert stereo AudioBuffer to mono Float32Array
 */
function getMonoSamples(audioBuffer: AudioBuffer): Float32Array {
  if (audioBuffer.numberOfChannels === 1) {
    return audioBuffer.getChannelData(0)
  }

  const left = audioBuffer.getChannelData(0)
  const right = audioBuffer.getChannelData(1)
  const mono = new Float32Array(left.length)

  for (let i = 0; i < left.length; i++) {
    mono[i] = (left[i] + right[i]) / 2
  }

  return mono
}

/**
 * Detect which AI model likely generated this audio
 * Based on common spectral signatures
 */
function detectSource(
  _samples: Float32Array,
  _sampleRate: number,
  sizzleScore: number,
  muddinessScore: number
): string | null {
  // This is heuristic-based detection
  // Suno tends to have specific high-frequency characteristics
  // Udio has different artifact patterns

  // Very simplified detection - in reality would need more sophisticated analysis
  if (sizzleScore > 0.5 && muddinessScore < 0.3) {
    return 'Suno (likely)'
  } else if (sizzleScore < 0.3 && muddinessScore > 0.5) {
    return 'Udio (likely)'
  }

  // Could expand with more model signatures
  return 'AI-Generated Audio'
}

/**
 * Main analysis function
 * Analyzes an audio file for AI artifacts
 */
export async function analyzeAudio(
  file: File,
  onProgress?: ProgressCallback
): Promise<ArtifactReport> {
  const report = async (step: string, percent: number, detail?: string) => {
    onProgress?.({ step, percent, detail })
    // Ensure minimum visibility of each step
    await sleep(MIN_STEP_DELAY)
  }

  await report('Loading audio', 0, 'Decoding audio file...')

  // Decode audio file
  const audioContext = new AudioContext()
  const arrayBuffer = await file.arrayBuffer()
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)

  const sampleRate = audioBuffer.sampleRate
  const duration = audioBuffer.duration
  const samples = getMonoSamples(audioBuffer)

  await report('Analyzing spectrum', 15, 'Calculating sizzle score...')

  // Calculate sizzle
  const sizzleScore = calculateSizzle(samples, sampleRate)

  await report('Analyzing spectrum', 30, 'Calculating muddiness...')

  // Calculate muddiness
  const muddinessScore = calculateMuddiness(samples, sampleRate)

  await report('Detecting artifacts', 45, 'Scanning for clicks and pops...')

  // Detect clicks
  const { count: clickCount, positions: clickPositions } = detectClicks(
    samples,
    sampleRate
  )

  await report('Detecting artifacts', 55, `Found ${clickCount} transient anomalies`)

  await report('Measuring dynamics', 65, 'Calculating dynamic range...')

  // Calculate dynamic range
  const dynamicRangeDb = calculateDynamicRange(samples)

  await report('Measuring dynamics', 75, `Dynamic range: ${dynamicRangeDb.toFixed(1)} dB`)

  await report('Measuring loudness', 85, 'Calculating integrated LUFS...')

  // Calculate LUFS
  const lufs = calculateLUFS(audioBuffer)

  await report('Generating report', 92, 'Identifying source...')

  // Detect source
  const detectedSource = detectSource(samples, sampleRate, sizzleScore, muddinessScore)

  await report('Generating report', 96, 'Computing quality score...')

  // Calculate overall score
  const overallScore = calculateOverallScore(
    sizzleScore,
    muddinessScore,
    clickCount,
    dynamicRangeDb,
    lufs
  )

  // Generate recommendations
  const recommendations = generateRecommendations(
    sizzleScore,
    muddinessScore,
    clickCount,
    dynamicRangeDb,
    lufs
  )

  await report('Complete', 100, 'Analysis complete!')

  // Close audio context to free resources
  await audioContext.close()

  return {
    sizzleScore,
    muddinessScore,
    clickCount,
    clickPositions,
    dynamicRangeDb,
    lufs,
    overallScore,
    duration,
    detectedSource,
    recommendations,
  }
}

/**
 * Calculate overall quality score (0-100)
 */
function calculateOverallScore(
  sizzle: number,
  muddiness: number,
  clicks: number,
  dynamicRange: number,
  lufs: number
): number {
  // Weights (total = 100)
  const weights = {
    sizzle: 25, // High-freq artifacts are very noticeable
    muddiness: 20, // Muddy low-end is common in AI music
    clicks: 20, // Clicks are jarring
    dynamicRange: 15, // Over-compression is common
    lufs: 20, // Wrong loudness = rejection on platforms
  }

  // Score each metric (higher = better)
  const sizzleScore = Math.max(0, 1 - sizzle * 1.5) * weights.sizzle
  const muddinessScore = Math.max(0, 1 - muddiness * 1.2) * weights.muddiness
  const clicksScore = Math.max(0, 1 - clicks / 20) * weights.clicks
  const drScore = Math.min(1, dynamicRange / 12) * weights.dynamicRange

  // LUFS: -14 is ideal for streaming
  const lufsDiff = Math.abs(lufs + 14)
  const lufsScore = Math.max(0, 1 - lufsDiff / 10) * weights.lufs

  return Math.round(
    sizzleScore + muddinessScore + clicksScore + drScore + lufsScore
  )
}

/**
 * Generate human-readable recommendations based on analysis
 */
function generateRecommendations(
  sizzleScore: number,
  muddinessScore: number,
  clickCount: number,
  dynamicRangeDb: number,
  currentLufs: number
): string[] {
  const recommendations: string[] = []

  // Sizzle
  if (sizzleScore > 0.6) {
    recommendations.push(
      'High sizzle detected (harsh 5-8kHz frequencies). Apply de-essing or reduce high-frequency harshness in mastering.'
    )
  } else if (sizzleScore > 0.3) {
    recommendations.push(
      'Moderate sizzle detected. Consider gentle high-frequency reduction with a shelf EQ around 6-8kHz.'
    )
  }

  // Muddiness
  if (muddinessScore > 0.6) {
    recommendations.push(
      'Significant low-frequency muddiness. Apply high-pass filter around 30-50Hz and reduce sub-bass energy.'
    )
  } else if (muddinessScore > 0.3) {
    recommendations.push(
      'Some low-end buildup detected. Consider subtle EQ cuts below 100Hz to improve clarity.'
    )
  }

  // Clicks
  if (clickCount > 20) {
    recommendations.push(
      `Many clicks/pops detected (${clickCount}). Use click removal or de-clicking tool to clean these transient artifacts.`
    )
  } else if (clickCount > 5) {
    recommendations.push(
      `Some clicks detected (${clickCount}). Consider using click removal to clean up these artifacts.`
    )
  }

  // Dynamic range
  if (dynamicRangeDb < 6) {
    recommendations.push(
      `Very low dynamic range (${dynamicRangeDb.toFixed(1)} dB). Audio appears over-compressed. Consider using gentler compression settings.`
    )
  } else if (dynamicRangeDb < 10) {
    recommendations.push(
      `Reduced dynamic range (${dynamicRangeDb.toFixed(1)} dB). Use light compression to preserve more dynamics.`
    )
  }

  // Loudness
  const targetLufs = -14.0
  const lufsDiff = currentLufs - targetLufs

  if (currentLufs < -20) {
    recommendations.push(
      `Audio is quiet (${currentLufs.toFixed(1)} LUFS). Normalize to ${targetLufs} LUFS for optimal streaming playback.`
    )
  } else if (lufsDiff > 2) {
    recommendations.push(
      `Audio is loud (${currentLufs.toFixed(1)} LUFS). Will be turned down by streaming platforms. Target: ${targetLufs} LUFS.`
    )
  } else if (Math.abs(lufsDiff) > 1) {
    recommendations.push(
      `Current loudness: ${currentLufs.toFixed(1)} LUFS. Consider normalizing to ${targetLufs} LUFS for streaming platforms.`
    )
  }

  if (recommendations.length === 0) {
    recommendations.push(
      'Audio analysis looks good! No major artifacts detected. Your track appears release-ready.'
    )
  }

  return recommendations
}

export { STREAMING_TARGETS }
